import React from "react";
import TextInput from "../../../../library/TextInput";
import SearchableSelectDropdown from "../../../../library/SearchableSelectDropdown";
import ToggleSwitch from "../../../../library/ToggleSwitch";
import PermissionsGate from "../../../../../utils/permissionGate";
import { StyledPanel, StyledPanelBody, StyledPanelSection, StyledPanelTitle } from "./VersionDetailsForm.styles";

/*
 * Node 164:120836's Notes field is a fixed 96px. TextInput's `multiline` mode only sets
 * `minHeight` from its size preset, so this pins `height` (and `boxSizing`, so 96px is the
 * full outer height, padding/border included) via the inline style it spreads onto the
 * underlying textarea.
 */
const NOTES_TEXTAREA_STYLE = { height: "96px", minHeight: "96px", boxSizing: "border-box" };

/**
 * Version Details' left-hand "Version Settings" panel (SPEC2 R1): Name,
 * the searchable Qualification Co-ordinator picker, Notes, and the Lock
 * Version Settings toggle. Name/Co-ordinator/Notes are disabled - with a
 * reason - whenever the version is locked or the user only has view access;
 * the lock toggle itself is only gated by its own permission (below).
 * @param {boolean} isVersionLocked Whether the version's settings are locked.
 * @param {boolean} hasEditAccess Whether the user has edit (not view-only) access.
 */
function VersionSettingsPanel({ formik, coordinatorOptions, isCoordinatorLoading, isVersionLocked, hasEditAccess }) {
    const fieldsDisabled = isVersionLocked || !hasEditAccess;
    const disabledReason = isVersionLocked ? "This version's settings are locked" : "Insufficient Permissions";

    return (
        <StyledPanel>
            <StyledPanelSection>
                <StyledPanelTitle>Version Settings</StyledPanelTitle>

                <StyledPanelBody>
                    <TextInput
                        label="Name"
                        required
                        id="name"
                        name="name"
                        value={formik.values.name}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        error={Boolean(formik.touched.name && formik.errors.name)}
                        errorText={formik.touched.name ? formik.errors.name : undefined}
                        disabled={fieldsDisabled}
                        disabledReason={disabledReason}
                    />

                    <SearchableSelectDropdown
                        label="Qualification Co-ordinator"
                        placeholder="Select Qualification Co-ordinator"
                        id="coordinator_id"
                        name="coordinator_id"
                        value={formik.values.coordinator_id}
                        onChange={(event) => formik.setFieldValue("coordinator_id", event.target.value)}
                        options={coordinatorOptions}
                        loading={isCoordinatorLoading}
                        clearable
                        emptyMessage="No matching staff"
                        disabled={fieldsDisabled}
                        disabledReason={disabledReason}
                    />

                    <TextInput
                        label="Notes"
                        id="notes"
                        name="notes"
                        multiline
                        rows={3}
                        value={formik.values.notes}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        style={NOTES_TEXTAREA_STYLE}
                        disabled={fieldsDisabled}
                        disabledReason={disabledReason}
                    />

                    <PermissionsGate
                        scopes={["pmcversionlocksettingedit"]}
                        errorProps={{ disabled: true, disabledReason: "Insufficient Permissions" }}
                    >
                        <ToggleSwitch
                            id="lock_version_settings"
                            name="lock_version_settings"
                            label="Lock Version Settings"
                            description="Prevents modification to linked modules, study path and version settings"
                            checked={formik.values.lock_version_settings === 1}
                            onChange={(event) => formik.setFieldValue("lock_version_settings", event.target.checked ? 1 : 0)}
                        />
                    </PermissionsGate>
                </StyledPanelBody>
            </StyledPanelSection>
        </StyledPanel>
    );
}

export default VersionSettingsPanel;
